import { useMemo, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import type { ProjectGraphNode } from '@avisoma/shared';
import { Badge } from '../../catalyst/badge';
import { Button } from '../../catalyst/button';
import { Input } from '../../catalyst/input';
import { Select } from '../../catalyst/select';
import { AuthorityChips } from './AuthorityChips';
import { GROUP_PAGE, nodeLabel, type GroupViewNode } from './graphModel';
import { authoritiesOf, memberKindOf, sortMembers, sortModesOf, withAuthority, type SortMode } from './memberSort';
import { NodeButton } from './NodeButton';
import { SelectionHeader } from './SelectionHeader';
import { VIRTUAL_MIN, VirtualList } from './VirtualList';

type Props = {
  group: GroupViewNode;
  nodesById: ReadonlyMap<string, ProjectGraphNode>;
  visibleIds: ReadonlySet<string>;
  /** Buttons shown beside the group badge. */
  actions?: ReactNode;
  /** The list's order; kept per group kind by the parent, so the next group of the kind keeps it. */
  sort: SortMode;
  onSort: (mode: SortMode) => void;
  /** Shows the member on the canvas, selects it, and pans to it. */
  onReveal: (memberId: string) => void;
  /** Shows the group's next GROUP_PAGE members on the canvas. */
  onShowMore: () => void;
};

const count = (value: number) => value.toLocaleString('en-US');
/** A member row's fixed height in a windowed list, spacing included (`h-8`). */
const MEMBER_ROW_HEIGHT = 32;
const nodeKey = (node: ProjectGraphNode) => node.id;

/**
 * Every member of the selected group, best-connected first or in the chosen sort, with a filter over their labels
 * (a task label names its description). A reference group also filters by authority.
 */
export function GroupMembers({ group, nodesById, visibleIds, actions, sort, onSort, onReveal, onShowMore }: Props) {
  const [filter, setFilter] = useState('');
  const [authority, setAuthority] = useState<string | null>(null);
  const kind = memberKindOf(group.groupKey);
  const modes = sortModesOf(kind);
  // A mode the group does not offer (none today) reads as the group's own order.
  const mode = modes.some((m) => m.mode === sort) ? sort : 'connections';
  const { sorted, authorities } = useMemo(() => {
    const all = group.memberIds.flatMap((id) => nodesById.get(id) ?? []);
    const references = memberKindOf(group.groupKey) === 'reference';
    return { sorted: sortMembers(all, mode), authorities: references ? authoritiesOf(all) : [] };
  }, [group.memberIds, group.groupKey, nodesById, mode]);
  // A chosen authority the group no longer holds (a refresh dropped its last member) has no chip to clear it: All.
  const chosenAuthority = authority !== null && authorities.includes(authority) ? authority : null;
  const needle = filter.trim().toLowerCase();
  const members = withAuthority(sorted, chosenAuthority).filter(
    (node) => needle === '' || nodeLabel(node).toLowerCase().includes(needle),
  );
  // A windowed list needs rows of one height, so long labels are cut to one line there.
  const windowed = members.length > VIRTUAL_MIN;
  const status =
    group.shown === 0 ? 'None shown' : group.shown >= group.total ? 'All shown' : `${count(group.shown)} shown`;

  return (
    <div className="flex flex-col gap-3 text-sm text-zinc-900 dark:text-zinc-100">
      <SelectionHeader kind="group">{actions}</SelectionHeader>
      <div>
        <div className="font-medium break-words">
          {group.label} · {count(group.total)}
        </div>
        <div className="text-zinc-500 dark:text-zinc-400">{status}. Pick one to show it.</div>
      </div>
      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <Input
            type="search"
            aria-label="Filter the members"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div className="w-36 shrink-0">
          <Select aria-label="Sort" value={mode} onChange={(e) => onSort(e.target.value as SortMode)}>
            {modes.map((m) => (
              <option key={m.mode} value={m.mode}>
                {m.label}
              </option>
            ))}
          </Select>
        </div>
      </div>
      {authorities.length > 0 && <AuthorityChips authorities={authorities} value={chosenAuthority} onChange={setAuthority} />}
      {members.length === 0 ? (
        <p className="text-zinc-500 dark:text-zinc-400">No matches.</p>
      ) : (
        <VirtualList
          items={members}
          getKey={nodeKey}
          rowHeight={MEMBER_ROW_HEIGHT}
          spacing={1}
          boxed
          rowClassName={clsx('flex items-center gap-2', windowed && 'h-8')}
          renderRow={(node) => (
            <>
              <NodeButton label={nodeLabel(node)} truncate={windowed} fill onClick={() => onReveal(node.id)} />
              {visibleIds.has(node.id) && <Badge color="indigo">shown</Badge>}
            </>
          )}
        />
      )}
      {group.remaining > 0 && (
        <div>
          <Button outline onClick={onShowMore}>
            Show {GROUP_PAGE} more
          </Button>
        </div>
      )}
    </div>
  );
}
